import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useQuestionHandlers } from "@/lib/dnd/hooks/useQuestionHandlers";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { answerImageStyles } from "@ai-teach/canvas-styles";
import type { AnswerImageElement } from "@ai-teach/types";
import { AiFillPicture } from "react-icons/ai";
import { FaMicrophone } from "react-icons/fa";
import { MdCancel } from "react-icons/md";

interface AnswerImageRendererProps {
  element: AnswerImageElement;
  isLiveMode?: boolean;
}

export function AnswerImageRenderer({ element, isLiveMode }: AnswerImageRendererProps) {
  const dispatch = useAppDispatch();
  const { handleQuestionDragAndDrop } = useQuestionHandlers();
  const { handleFileDrop } = useLocalImageUpload((media, containerId) =>
    handleQuestionDragAndDrop(media, containerId)
  );

  // const handleDeleteImage = (event: React.MouseEvent) => {
  //   event.stopPropagation();
  //   dispatch(
  //     updateElementProps({
  //       id: element.id,
  //       props: {
  //         media: { id: null, src: null, filename: null },
  //       },
  //     })
  //   );
  // };

  return (
    <div style={answerImageStyles.outerDiv}>
      <div
        contentEditable={!isLiveMode}
        data-no-moveable-drag
        suppressContentEditableWarning
        onMouseDown={(e) => {
          if (!isLiveMode) e.currentTarget.focus();
        }}
        onBlur={(e) => {
          dispatch(
            updateElementProps({
              id: element.id,
              props: { questionText: e.currentTarget.innerText },
            })
          );
        }}
        style={answerImageStyles.questionContainer}
        className="outline-none"
      >
        {element.questionText}
      </div>

      {isLiveMode ? (
        <div style={answerImageStyles.imageContainer}>
          {element.media?.src ? (
            <img
              draggable={false}
              src={element.media.src}
              alt={element.media?.filename ?? "answer image"}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="bg-white w-full h-full flex flex-col items-center justify-center">
              <AiFillPicture className="text-3xl text-slate-400" />
            </div>
          )}
        </div>
      ) : (
        <DroppableZone
          id={`answerImage-${element.id}`}
          data={{ id: `question//answerImage//${element.id}//0`, type: "imageChoice" }}
          allowedType={["image"]}
          style={answerImageStyles.imageContainer}
          defaultBorderClass={element.media.src ? "" : "border-2 border-dashed border-slate-300"}
          onLocalFileDrop={(file) =>
            handleFileDrop(file, `question//answerImage//${element.id}//0`)
          }
        >
          {element.media?.src ? (
            <img
              draggable={false}
              src={element.media.src}
              alt={element.media?.filename ?? "answer image"}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center justify-center">
              <AiFillPicture className="object-contain rounded" fontSize={40} />
              <p className="text-sm">Drop image here</p>
            </div>
          )}
        </DroppableZone>
      )}

      <div style={answerImageStyles.microphoneContainer}>
        <FaMicrophone />
      </div>

      <div
        style={{
          ...answerImageStyles.answerContainer,
          visibility: isLiveMode ? "hidden" : "visible",
        }}
      >
        <div
          contentEditable={!isLiveMode}
          data-no-moveable-drag
          suppressContentEditableWarning
          onMouseDown={(e) => {
            if (!isLiveMode) e.currentTarget.focus();
          }}
          onBlur={(e) => {
            dispatch(
              updateElementProps({
                id: element.id,
                props: { correctAnswer: e.currentTarget.innerText },
              })
            );
          }}
          className="outline-none"
        >
          {element.correctAnswer}
        </div>
        {!isLiveMode && (
          <p className="text-[10px] text-error">This block will not shown on live view</p>
        )}
      </div>
    </div>
  );
}
